import { EventEmitter } from 'events';
import {
  type BoundKey,
  type Bounds,
  type NonEmptyArray,
  bounds,
  nonEmptyArray,
} from '@ipfabric/utils';
import { bindMethod, callMethod, wrap } from '@ipfabric/utils/fp/function';
import { keepIdentityIfEqual } from '@ipfabric/utils/fp/keepIdentityIfEqual';
import { multiply } from '@ipfabric/utils/fp/number';
import { ___, partialApply } from '@ipfabric/utils/fp/partialApply';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { setPropTo } from '@ipfabric/utils/fp/prop';
import { array, option, readonlyArray } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import { flow, pipe, tupled } from 'fp-ts/lib/function';
import type { TypedEventEmitter } from 'utils/events/TypedEventEmitter';
import { boundsEq } from 'utils/pixi/Bounds';

export interface BoundsChangeEvent {
  oldBounds: Option<Bounds>;
  newBounds: Option<Bounds>;
}

export type BoundsCalculatorEvents = {
  change: (event: BoundsChangeEvent) => void;
};

type ObjectsBounds = Map<string, Bounds>;

const getBound = (objects: ObjectsBounds, key: BoundKey): Option<number> => {
  const positiveness = bounds.sign[key];
  return pipe(
    objects,
    plainMap.reduce(Number.NEGATIVE_INFINITY, (acc, [, objectBounds]) =>
      Math.max(acc, objectBounds[key] * positiveness),
    ),
    multiply(positiveness),
    option.fromPredicate(Number.isFinite),
  );
};

const recalculateBounds = (objects: ObjectsBounds, lastBounds: Bounds) =>
  flow(
    readonlyArray.reduce(
      option.some(lastBounds),
      (maybeBounds: Option<Bounds>, key: BoundKey) =>
        pipe(
          maybeBounds,
          option.chain((nextBounds) =>
            pipe(
              getBound(objects, key),
              option.map(setPropTo(nextBounds, key)),
            ),
          ),
        ),
    ),
  );

const updateBoundsAffectedByRemoval =
  (objects: ObjectsBounds, objectBounds: Bounds) =>
  (globalBounds: Bounds): Option<Bounds> =>
    pipe(
      bounds.keys,
      // find directions in which the element lies at max position
      readonlyArray.filter((key) => globalBounds[key] === objectBounds[key]),
      recalculateBounds(objects, globalBounds),
    );

const mergeBounds = (...inputBounds: NonEmptyArray<Bounds>) => {
  return inputBounds.reduce((result, current) =>
    pipe(
      bounds.keys,
      readonlyArray.reduce(result, (acc, key) => {
        if (Math.sign(current[key] - acc[key]) === bounds.sign[key]) {
          // clone only once and only when necessary
          const targetAcc =
            acc === inputBounds[0] ? { ...inputBounds[0] } : acc;
          targetAcc[key] = current[key];
          return targetAcc;
        }
        return acc;
      }),
    ),
  );
};

const optionBoundsEq = option.getEq(boundsEq);

const makeWatchBoundsChange = (
  getBounds: () => Option<Bounds>,
  events: TypedEventEmitter<BoundsCalculatorEvents>,
) =>
  wrap((cb: () => void): void => {
    const oldBounds = getBounds();
    cb();
    const newBounds = getBounds();
    if (!optionBoundsEq.equals(oldBounds, newBounds)) {
      events.emit('change', { oldBounds, newBounds });
    }
  });

export class BoundsCalculator {
  private readonly objects = new Map<string, Bounds>();

  private globalBounds: Option<Bounds> = option.none;

  public getBounds = (): Option<Bounds> => this.globalBounds;

  private readonly events: TypedEventEmitter<BoundsCalculatorEvents> =
    new EventEmitter();

  private removeObjectBoundsRaw = (id: string) =>
    pipe(
      this.objects,
      plainMap.get(id),
      option.map((boundsToRemove) => {
        this.objects.delete(id);
        this.removeBounds(boundsToRemove);
      }),
    );

  public addListener = bindMethod(this.events, 'addListener');

  public removeListener = bindMethod(this.events, 'removeListener');

  private watchBoundsChange = makeWatchBoundsChange(
    () => this.globalBounds,
    this.events,
  );

  public removeObjectBounds = this.watchBoundsChange(
    this.removeObjectBoundsRaw,
  );

  public setObjectBounds = this.watchBoundsChange(
    (id: string, newBounds: Bounds): void => {
      this.removeObjectBoundsRaw(id);
      this.objects.set(id, newBounds);
      this.addToBounds(newBounds);
    },
  );

  public getObjectIds = (): IterableIterator<string> => this.objects.keys();

  private updateBoundsAffectedByRemoval = (
    objectBounds: Bounds,
  ): Option<Bounds> =>
    pipe(
      this.globalBounds,
      option.chain(updateBoundsAffectedByRemoval(this.objects, objectBounds)),
    );

  private removeBounds(boundsToRemove: Bounds) {
    this.globalBounds = this.updateBoundsAffectedByRemoval(boundsToRemove);
  }

  private addToBounds(objectBounds: Bounds) {
    this.globalBounds = pipe(
      this.globalBounds,
      option.fold(
        () => objectBounds,
        partialApply(mergeBounds, ___, objectBounds),
      ),
      option.some,
    );
  }
}

/**
 * Merge results of multiple BoundsCalculator instances
 *
 * This is useful in case you have multiple types of entities that affect bounds,
 * for example a graph has nodes and edges. It allows you:
 * - simply avoid conflicts in IDs since each assigned calculator has its own map
 * - dynamically add/remove some calculators so you can e.g. temporarily remove edges
 *   from calculating bounds when graph is in some state that is not displaying edges.
 */
export class MergedBoundsCalculator {
  private readonly calculators = new Set<BoundsCalculator>();

  private readonly events: TypedEventEmitter<BoundsCalculatorEvents> =
    new EventEmitter();

  private globalBounds: Option<Bounds> = option.none;

  private dirty = false;

  public getBounds = (): Option<Bounds> => {
    if (this.dirty) {
      this.dirty = false;
      this.globalBounds = pipe(
        Array.from(this.calculators.values()),
        array.filterMap(callMethod('getBounds')),
        option.fromPredicate(nonEmptyArray.isNonEmpty),
        option.map(tupled(mergeBounds)),
        keepIdentityIfEqual(optionBoundsEq)(this.globalBounds),
      );
    }
    return this.globalBounds;
  };

  public addListener = bindMethod(this.events, 'addListener');

  public removeListener = bindMethod(this.events, 'removeListener');

  private watchBoundsChange = makeWatchBoundsChange(
    this.getBounds,
    this.events,
  );

  private handleBoundsChange = this.watchBoundsChange(() => {
    this.dirty = true;
  });

  public addCalculator = this.watchBoundsChange(
    (calculator: BoundsCalculator) => {
      this.calculators.add(calculator);
      calculator.addListener('change', this.handleBoundsChange);
      this.dirty = true;
    },
  );

  public removeCalculator = this.watchBoundsChange(
    (calculator: BoundsCalculator) => {
      calculator.removeListener('change', this.handleBoundsChange);
      this.calculators.delete(calculator);
      this.dirty = true;
    },
  );
}
